/**
 * PaneSplitter — the draggable + keyboard-resizable divider between the map and
 * reference panes (spec §3.2). 4px visual rail → 12px hit-rail (CSS), col-resize
 * cursor, min/max clamp, remembers width (the parent persists to localStorage).
 *
 * A11y (DS §2.7 / WCAG 2.2): role="separator", aria-orientation="vertical",
 * aria-valuemin/max/now (the left pane's width %), focusable, arrow-keys resize
 * in 2% steps, Home/End jump to the clamps. Drag is never the only path.
 *
 * TOKENS ONLY — visual styling lives in evaluate-workspace.css (.evalws-splitter).
 */
import { useCallback, useRef } from 'react';

export default function PaneSplitter({
  value,
  min = 35,
  max = 75,
  step = 2,
  onChange,
}: {
  /** the LEFT pane width as a percentage of the panes row (clamped to min/max). */
  value: number;
  min?: number;
  max?: number;
  step?: number;
  onChange: (pct: number) => void;
}) {
  const railRef = useRef<HTMLButtonElement | null>(null);
  const draggingRef = useRef(false);

  const clamp = useCallback(
    (n: number) => Math.max(min, Math.min(max, n)),
    [min, max],
  );

  // translate a clientX into a left-pane percentage, measured against the parent
  // .evalws-panes row (the splitter's parent element).
  const pctFromClientX = useCallback((clientX: number): number | null => {
    const rail = railRef.current;
    const row = rail?.parentElement;
    if (!row) return null;
    const rect = row.getBoundingClientRect();
    if (rect.width <= 0) return null;
    return ((clientX - rect.left) / rect.width) * 100;
  }, []);

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLButtonElement>) => {
      // ignore secondary buttons.
      if (e.button !== 0) return;
      draggingRef.current = true;
      railRef.current?.setPointerCapture(e.pointerId);
      e.preventDefault();
    },
    [],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent<HTMLButtonElement>) => {
      if (!draggingRef.current) return;
      const pct = pctFromClientX(e.clientX);
      if (pct == null) return;
      onChange(clamp(pct));
    },
    [pctFromClientX, onChange, clamp],
  );

  const endDrag = useCallback(
    (e: React.PointerEvent<HTMLButtonElement>) => {
      if (!draggingRef.current) return;
      draggingRef.current = false;
      try {
        railRef.current?.releasePointerCapture(e.pointerId);
      } catch {
        /* capture may already be gone — harmless */
      }
    },
    [],
  );

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>) => {
      let next: number | null = null;
      if (e.key === 'ArrowLeft') next = value - step;
      else if (e.key === 'ArrowRight') next = value + step;
      else if (e.key === 'Home') next = min;
      else if (e.key === 'End') next = max;
      if (next == null) return;
      e.preventDefault();
      onChange(clamp(next));
    },
    [value, step, min, max, onChange, clamp],
  );

  return (
    <button
      ref={railRef}
      type="button"
      className="evalws-splitter"
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize the map and reference panes"
      aria-valuemin={min}
      aria-valuemax={max}
      aria-valuenow={Math.round(value)}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onKeyDown={onKeyDown}
    />
  );
}
